/**
 * Avalon operational API, same origin /api. This client is not an authority:
 * authentication, visibility, transitions and idempotency are server enforced.
 * HttpOnly sessions; CSRF only in memory. No service credentials in the bundle.
 * Engine/Ava, CRM, Tree and credits are reached exclusively through the service.
 */
export type Visibility = "private" | "members";
export interface Profile {
  person_id: string;
  display_name: string;
  bio: string;
  offered_skills: string[];
  visibility: Visibility;
  remote: boolean;
  place_ids: string[];
  revision: string;
}
export interface Session {
  person: { id: string; display_name: string } | null;
  role: "member" | "staff" | "crm" | null;
  profile: Profile | null;
  csrf_token: string | null;
  auth?: {status: "awaiting_confirmation"; message: string};
}
export interface NeedDraft {
  help_terms: string[];
  context: string;
  distance_preference: "any" | "remote" | "onsite";
  place_id: string | null;
  require_available: boolean;
  visibility_scope: Visibility;
}
export interface Need extends NeedDraft {
  id: string;
  status?: string;
  created_at?: string;
}
export interface Suggestion {
  person_id: string;
  display_name: string;
  reason: string;
  matched_skills: string[];
  availability: {
    status: "declared_available" | "not_declared";
    until: number | null;
  };
}
export interface Suggestions {
  status: "suggestions" | "no_match" | "partial" | "unavailable";
  suggestions: Suggestion[];
  limitations?: string[];
}
export interface Introduction {
  id: string;
  need_id: string;
  sender_person_id: string;
  recipient_person_id: string;
  sender_name: string;
  recipient_name: string;
  context: string;
  status: "pending" | "accepted" | "declined" | "withdrawn" | "expired";
  expires_at: string;
  crm_person_id?: string;
  tree_references?: Array<{
    id?: string;
    label?: string;
    reference_id?: string;
    node_id?: string;
    catalog_id?: string;
    provenance?: string;
    justification?: string;
    synthetic?: boolean;
  }>;
}
export interface Catalog {
  places: Array<{ id: string; name: string; description: string; address?: string; opening_hours?: string; equipment?: string[]; access_conditions?: string }>;
  events: Array<{
    id: string;
    title: string;
    place_id: string;
    starts_at: string;
    description?: string; ends_at?: string; format?: "onsite" | "remote" | "hybrid"; joining_details?: string;
  }>;
  spaces: Array<{ id: string; name: string; place_id: string; capacity?: number; equipment?: string[]; conditions?: string; status?: string }>;
  demo: boolean;
}
export interface CrmContact {
  id: string;
  person_id: string;
  display_name: string;
  marketing_consent: boolean;
  notes: Array<{ id: string; body: string }>;
  tree_references: Array<{
    id: string;
    catalog_id: string;
    node_id: string;
    provenance: string;
    justification: string;
    synthetic: boolean | number;
  }>;
}
export interface MembershipOffer {
  id: string; label: string; status: string; terms: Record<string, unknown>;
}
export interface Membership {
  demo?: boolean;
  status: string;
  offers: MembershipOffer[];
  entitlements: Array<{key: string; value: unknown}>;
  subscription: {status: string; management_status: string};
  subscription_intent: {status: string; created_at: number} | null;
  credits: {status: string};
}
export interface Preferences {
  notifications: {introductions: boolean};
  discovery_personalization: boolean;
}
export interface Notification {
  id: string; kind: string; resource_id: string; created_at: number; read_at: number | null;
}

let csrfToken: string | null = null;
let sessionGeneration = 0;
let sessionQueue: Promise<unknown> = Promise.resolve();
const expiredListeners = new Set<() => void>();
export function onSessionExpired(listener: () => void) {
  expiredListeners.add(listener);
  return () => { expiredListeners.delete(listener); };
}
export class ApiError extends Error {
  status: number;
  code?: string;
  constructor(
    message: string,
    status: number,
    code?: string,
  ) {
    super(message);
    this.status = status;
    this.code = code;
  }
}
export async function request<T>(
  path: string,
  method = "GET",
  body?: unknown,
  idempotencyKey?: string,
): Promise<T> {
  const requestGeneration = sessionGeneration;
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      method,
      credentials: "include",
      headers: {
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
        ...(csrfToken && method !== "GET" ? { "X-CSRF-Token": csrfToken } : {}),
        ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch {
    throw new ApiError(
      "Le service est injoignable. Vérifiez votre connexion et réessayez.",
      0,
    );
  }
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    if (response.status === 401 && requestGeneration === sessionGeneration && !/^\/auth\/(signup|login|confirm|exchange)$/.test(path)) {
      sessionGeneration++;
      csrfToken = null;
      for (const listener of expiredListeners) listener();
    }
    const fallback =
      response.status === 401
        ? "Connectez-vous pour poursuivre."
        : response.status === 403
          ? "Votre compte ne dispose pas de cet accès."
          : "Cette action n’a pas abouti. Vous pouvez réessayer.";
    throw new ApiError(
      payload.message ||
        (typeof payload.error === "string"
          ? payload.error
          : payload.error?.message) ||
        fallback,
      response.status,
      typeof payload.error === "string" ? payload.error : undefined,
    );
  }
  return payload as T;
}
function sessionRequest(path: string, method = "GET", body?: unknown): Promise<Session> {
  // Serialize session mutations: an older logout response must not overwrite a
  // newer login cookie, and old business responses must not invalidate a new user.
  const result = sessionQueue.then(async () => {
    const session = await request<Session>(path, method, body);
    csrfToken = session.csrf_token;
    sessionGeneration++;
    return session;
  });
  sessionQueue = result.catch(() => {});
  return result;
}

export const api = {
  session: () => sessionRequest("/session"),
  signup: (body: { email: string; password: string; display_name: string }) =>
    sessionRequest("/auth/signup", "POST", body),
  login: (body: { email: string; password: string }) =>
    sessionRequest("/auth/login", "POST", body),
  confirm: (body: {email: string; token: string}) => sessionRequest("/auth/confirm", "POST", body),
  exchange: (body: {access_token: string; refresh_token: string}) => sessionRequest("/auth/exchange", "POST", body),
  preferences: () => request<{preferences: Preferences}>("/preferences"),
  savePreferences: (body: Preferences) => request<{preferences: Preferences}>("/preferences", "PUT", body),
  notifications: () => request<{notifications: Notification[]}>("/notifications"),
  readNotification: (id: string) => request<{notifications: Notification[]}>(`/notifications/${encodeURIComponent(id)}/read`, "POST", {}),
  logout: () => sessionRequest("/auth/logout", "POST", {}),
  saveProfile: (body: Partial<Profile>) =>
    request<Profile | { profile: Profile }>("/profile", "PATCH", body),
  needs: () => request<{ needs: Need[] }>("/needs"),
  createNeed: (body: NeedDraft) =>
    request<{ need: Need }>("/needs", "POST", body),
  suggestions: (id: string) =>
    request<Suggestions>(
      `/needs/${encodeURIComponent(id)}/suggestions`,
      "POST",
      {},
    ),
  introductions: () =>
    request<{ requests: Introduction[] }>("/introduction-requests"),
  introduce: (need_id: string, recipient_person_id: string, key: string) =>
    request<{ request: Introduction }>(
      "/introduction-requests",
      "POST",
      { need_id, recipient_person_id },
      key,
    ),
  respond: (id: string, action: "accept" | "decline" | "withdraw") =>
    request<{ request: Introduction }>(
      `/introduction-requests/${encodeURIComponent(id)}/respond`,
      "POST",
      { action },
    ),
  availability: () =>
    request<{ availability: { until: string | null } }>("/availability"),
  setAvailability: (available: boolean, minutes = 60) =>
    request<{ availability: { until: string | null } }>(
      "/availability",
      "PUT",
      { available, minutes },
    ),
  catalog: () => request<Catalog>("/catalog"),
  membership: () => request<Membership>("/membership"),
  subscribe: () =>
    request<{ status: string }>("/subscription-intents", "POST", {}),
  crmContacts: () => request<{ contacts: CrmContact[] }>("/crm/contacts"),
  staffRequests: () =>
    request<{ requests: Introduction[] }>("/staff/introduction-requests"),
};
